/*
 * Front-end patches over the Dante booking widget.
 *
 * Everything inside #dante is HTML rendered by forthvalley.dante.app and injected by
 * https://www.dante.app/Scripts/Client/v1.js - none of it is ours, and Dante offer no hook for
 * label overrides or a post-render callback. These four changes came out of FVC's September 2026
 * testing document and have been raised with Dante to be fixed at source; until then we patch the
 * DOM after each render.
 *
 * Because it is someone else's markup, every patch bails out silently when what it expects is not
 * there. Leaving Dante's output alone is always the correct fallback.
 */
(function () {
    'use strict';

    var root = document.getElementById('dante');

    if (!root || typeof MutationObserver === 'undefined') {
        return;
    }

    // "Welcome Jane Smith - Individual: Jane Smith". Only collapses when the two names match:
    // "- Company: Acme Ltd" says something the welcome does not, and must survive untouched.
    var WELCOME_PATTERN = /^(\s*welcome\s+)(.+?)(\s*[-–]\s*individual:\s*)(.+?)(\s*)$/i;

    var busy = false;

    function normalise(value) {
        return value.replace(/\s+/g, ' ').trim().toLowerCase();
    }

    /*
     * Re-casts replacement text the way the original was written, so a label that Dante styles with
     * text-transform and a label that is genuinely upper case both come out looking the same.
     */
    function matchCasing(original, replacement) {
        var letters = original.replace(/[^a-z]/gi, '');

        if (letters.length === 0) {
            return replacement;
        }

        if (letters === letters.toUpperCase()) {
            return replacement.toUpperCase();
        }

        if (letters === letters.toLowerCase()) {
            return replacement.toLowerCase();
        }

        return replacement;
    }

    function eachTextNode(within, callback) {
        var walker = document.createTreeWalker(within, NodeFilter.SHOW_TEXT, null, false);
        var nodes = [];
        var node;

        while ((node = walker.nextNode())) {
            nodes.push(node);
        }

        // Collected first: callbacks rewrite text, and mutating during the walk is asking for trouble.
        nodes.forEach(callback);
    }

    // Item 1 - the account header prints the member's name twice.
    function collapseDuplicateWelcome() {
        eachTextNode(root, function (node) {
            var match = WELCOME_PATTERN.exec(node.nodeValue);

            if (!match) {
                return;
            }

            if (normalise(match[2]) !== normalise(match[4])) {
                return;
            }

            node.nodeValue = match[1] + match[2] + match[5];
        });
    }

    // Item 3 - FVC want the payment step's button to say "Book course", not "Place order".
    function relabelPlaceOrder() {
        var button = document.getElementById('btncompletebooking');

        if (!button || button.dataset.fvcRelabelled === '1') {
            return;
        }

        eachTextNode(button, function (node) {
            if (!/place\s+order/i.test(node.nodeValue)) {
                return;
            }

            node.nodeValue = node.nodeValue.replace(/place\s+order/i, function (found) {
                return matchCasing(found, 'Book course');
            });

            button.dataset.fvcRelabelled = '1';
        });
    }

    /*
     * Item 5 - nothing on the confirmation step points anywhere onward. The link itself is rendered
     * by Booking.cshtml so the copy and the URL stay in Razor; all this does is show it on the
     * confirmation step and hide it everywhere else.
     */
    function toggleContinueBrowsing() {
        var link = document.getElementById('fvc-continue-browsing');

        if (!link) {
            return;
        }

        link.hidden = !/^#\/?confirmation/i.test(window.location.hash);
    }

    // Item 6 - no way to check what you typed when setting or entering a password.
    function addPasswordToggles() {
        var inputs = root.querySelectorAll('input[type="password"]');

        Array.prototype.forEach.call(inputs, function (input) {
            if (input.dataset.fvcPasswordToggle === '1') {
                return;
            }

            var container = input.parentElement;

            if (!container) {
                return;
            }

            input.dataset.fvcPasswordToggle = '1';
            container.classList.add('fvc-has-password-toggle');

            var button = document.createElement('button');
            button.type = 'button';
            button.className = 'fvc-password-toggle';
            button.setAttribute('aria-label', 'Show password');
            button.setAttribute('aria-pressed', 'false');
            button.innerHTML = '<i class="fa fa-eye" aria-hidden="true"></i>';

            button.addEventListener('click', function () {
                var reveal = input.getAttribute('type') === 'password';

                input.setAttribute('type', reveal ? 'text' : 'password');
                button.setAttribute('aria-pressed', reveal ? 'true' : 'false');
                button.setAttribute('aria-label', reveal ? 'Hide password' : 'Show password');
                button.innerHTML = reveal
                    ? '<i class="fa fa-eye-slash" aria-hidden="true"></i>'
                    : '<i class="fa fa-eye" aria-hidden="true"></i>';
            });

            input.insertAdjacentElement('afterend', button);
        });
    }

    function enhance() {
        if (busy) {
            return;
        }

        busy = true;

        try {
            collapseDuplicateWelcome();
            relabelPlaceOrder();
            toggleContinueBrowsing();
            addPasswordToggles();
        } catch (error) {
            // Dante's markup moved on without us. Their page still works; ours just stops patching it.
            if (window.console && window.console.warn) {
                window.console.warn('Dante booking enhancements skipped:', error);
            }
        } finally {
            busy = false;
        }
    }

    // Dante re-renders the whole of #dante on every step, with no callback to hook, so watch it.
    // enhance() is guarded and idempotent, which is what stops our own writes looping back round.
    var scheduled = false;

    var observer = new MutationObserver(function () {
        if (scheduled) {
            return;
        }

        scheduled = true;

        window.setTimeout(function () {
            scheduled = false;
            enhance();
        }, 0);
    });

    observer.observe(root, { childList: true, subtree: true });

    window.addEventListener('hashchange', enhance);

    enhance();
})();
